import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ProductService } from '../service/product-service';
import { ContentWithNotes, ContentElement } from '../utils/commonTypes';
import { ContentUtilsService } from '../utils/content.utils';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';

type ContentState = {
  selectedContentId: string | null;
  contentCache: ContentWithNotes[];
  contentElementsCache: { id: string, elements: ContentElement[] }[];
  contentNotesCache: { id: string, title: string, contentId:string }[] //duplication to make the life of computed signals easier
  isLoading: boolean;
};

const initialState: ContentState = {
  selectedContentId: null,
  contentCache: [], //memory cache
  contentElementsCache: [],
  contentNotesCache: [],
  isLoading: false,
};

export const ContentStore = signalStore(
  { providedIn: 'root' },
  
  withStorageSync({
    key: 'content-store-synced', // key used when writing to/reading from storage
    autoSync: true, // read from storage on init and write on state changes - `true` by default
    // select: (state: GJState) => Partial<GJState>, // projection to keep specific slices in sync
    // parse: (stateString: string) => State, // custom parsing from storage - `JSON.parse` by default
    // stringify: (state: User) => string, // custom stringification - `JSON.stringify` by default
    // storage: () => sessionstorage, // factory to select storage to sync with
  }),
  withState(initialState),

  /** ✅ Selector: Retrieves the selected content from the cache */
  withComputed(({ selectedContentId, contentCache, contentElementsCache }) => ({
    selectedContent: computed(() =>
      contentCache().find(entry => entry.id === selectedContentId()) ?? null
    ),
    selectedContentElements: computed(() =>
      contentElementsCache().find(entry => entry.id === selectedContentId())?.elements ?? null
    ),

  })),

  withMethods((store, service = inject(ProductService), contentUtils = inject(ContentUtilsService)) => ({
    // async hydrateContentCache() {
    //   if (productStore.selectedProduct()) {
    //     productStore.selectedProduct().modules.forEach(module => {
    //       console.log(`Caching content from module [${module.id}]`);
    //       const contentIds = module.contents.map(c => c.id);
    //       service.hydrateContentCache(contentIds);
    //     });
    //   }
    // },

    async reset() {
      patchState(store, { selectedContentId: null });
    },

    async loadContent(contentId: string, forceReload?: boolean): Promise<boolean> {
      // const cachedContent = store.contentCache().find(entry => entry.id === contentId);
      // if (cachedContent && !forceReload) {
      //   // ✅ Set `selectedContentId` without reloading if it's already cached
      //   patchState(store, { selectedContentId: contentId });
      //   return true;
      // }

      patchState(store, { isLoading: true });

      try {
        const contentPackage = await service.loadContent(contentId);
        if (!contentPackage) throw new Error('Failed to fetch content');

        //Parse elements
        const contentElements = contentUtils.parseContent(contentPackage.publishedData as string);

        //Generate video urls
        await contentUtils.generateVideoLinks(contentElements);

        //Find note and chat ids
        const chats = contentPackage.dynamicElements?contentPackage.dynamicElements.chats?.map(c => ({ id: c.id, alias: c.alias ?? '' })):[];
        const notes = contentPackage.dynamicElements?contentPackage.dynamicElements.notes?.map(n => ({ id: n.id, alias: n.alias ?? '' })):[];
        contentUtils.fillNoteAndChatIds(contentElements, chats, notes);

        // ✅ Update cache and set selected content
        patchState(store, {
          isLoading: false,
          selectedContentId: contentId, // ✅ Set `selectedContentId`
          contentCache: [
            ...store.contentCache().filter(entry => entry.id !== contentId), // Remove existing entry if exists
            contentPackage, // Add new entry
          ],
          contentElementsCache: [
            ...store.contentElementsCache().filter(entry => entry.id !== contentId), // Remove existing entry if exists
            { id: contentId, elements: contentElements }, // Add new entry
          ],
        });

        //Update content notes cache
        contentPackage.dynamicElements.notes.forEach(note => {
          patchState(store, {
            contentNotesCache: [
              ...store.contentNotesCache().filter(entry => entry.id !== note.id), // Remove existing entry if exists
              { id: note.id, contentId: contentId, title: note.title ?? '' }, // Add new entry
            ],
          });
        });

        return true;
      } catch (error) {
        console.error('Error loading content:', error);
        patchState(store, { isLoading: false });
        return false;
      }
    },
    getNoteById: (id: string | null) =>
      computed(() => id != null ? store.contentNotesCache().find(c => c.id === id) ?? null : null)
  }))
);



